import { Button, Form, Input, Select, Dropdown } from "antd";
import { MenuProps } from "antd";
import { TaskPrioritySchema } from "@/employee_tasks/types/task";
import type { CreateTaskInput, Task } from "@/employee_tasks/types/task";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  RollbackOutlined,
  SaveOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Popconfirm, Space } from "antd";
import React, { useMemo, useCallback } from "react";
import { priorityNames } from "../columns";
import {
  useCreateEvaluation,
  useEmployeeTaskMutations,
  useEvaluationOptions,
} from "employee_tasks/hooks/useTasks";

import {
  useCanDelete,
  useCanEditTemplate,
  useCanEvaluate,
} from "employee_tasks/components/utils";
import { TemplateSelect } from "employee_tasks/components/template/TemplateSelect";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { PeopleCardTag } from "../../../../tmcard/styles";

export interface EditableCellProps {
  children: React.ReactNode;
  dataIndex: keyof Task | (keyof Task)[];
  record: Task | Partial<Task>;
  handleDeleteNotSavedTask?: (task?: EditableCellProps["record"]) => void;
  employeeId: number;
  positionId?: number;
}
const useTranslation = () => {
  return { t: (text: any) => text };
};
const btnShared = {
  size: "small" as SizeType,
  style: { border: 0, backgroundColor: "transparent" },
};

export const EditableCell: React.FC<EditableCellProps> = ({
  children,
  dataIndex,
  record,
  employeeId,
  positionId,
  handleDeleteNotSavedTask,
  ...restProps
}) => {
  const form = Form.useFormInstance();
  const isNew = record?.id && record.id.startsWith("new");
  const editing: boolean = Form.useWatch("editing", form) ?? isNew;
  const canEvaluate = useCanEvaluate(employeeId);
  const canDelete = useCanDelete(employeeId);
  const canEditTemplate = useCanEditTemplate(employeeId);
  const { t } = useTranslation();

  const { create, update, remove, ignore } =
    useEmployeeTaskMutations(employeeId);
  const { data: evaluationOptions } = useEvaluationOptions();
  const createEvaluation = useCreateEvaluation(employeeId);

  // region delete logic
  const isEmployeeTask = record?.source_object_type === "employee";
  const handleDeleteOrIgnore = useCallback(async () => {
    if (!isNew && record && record.id !== undefined) {
      if (isEmployeeTask) {
        await remove.mutateAsync(record.id);
      } else {
        await ignore.mutateAsync({ id: record.id });
      }
    }
  }, [isNew, record, isEmployeeTask, remove, ignore]);
  // endregion
  // region editing
  React.useEffect(() => {
    if (editing && record !== undefined) {
      if (dataIndex.toString() === "priority")
        form.setFieldsValue({ priority: record.priority });
      if (dataIndex.toString() === ["template_task", "name"].toString())
        form.setFieldsValue({ template_task_id: record?.template_task?.id });
    }
  }, [dataIndex, editing, form, record]);

  const toggleEditing = useCallback(() => {
    form.setFieldValue("editing", !editing);
  }, [form, editing]);

  const disableEdit = useCallback(() => {
    form.setFieldValue("editing", false);
  }, [form]);

  const handleSubmit = async () => {
    try {
      const { evaluation, ...values } = await form.validateFields();
      const data: CreateTaskInput = {
        ...values,
      };

      if (!isNew && record.id !== undefined) {
        await update.mutateAsync({ id: record.id, data });
      } else {
        const createdTask = await create.mutateAsync(data);
        if (evaluation) {
          await createEvaluation.mutateAsync({
            evaluation_option_id: evaluation,
            task_id: createdTask.id,
            template_task_id: createdTask.template_task.id,
          });
        }
      }
      disableEdit();
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleCancelEdit = () => {
    if (isNew) {
      handleDeleteNotSavedTask?.(record);
    } else {
      disableEdit();
    }
  };

  const menuItems = useMemo((): MenuProps["items"] => {
    const items: MenuProps["items"] = [];

    if (isEmployeeTask) {
      items.push({
        key: "edit",
        label: t("Editar"),
        icon: <EditOutlined />,
        onClick: toggleEditing,
      });
    }

    if (canDelete) {
      if (items.length > 0) {
        items.push({
          type: "divider",
        });
      }

      items.push({
        key: "deleteOrIgnore",
        label: (
          <Popconfirm
            title={
              isEmployeeTask
                ? t("Deletar essa tarefa?")
                : t("Ocultar essa tarefa do cargo?")
            }
            okType="danger"
            placement="left"
            okText={isEmployeeTask ? t("Deletar") : t("Esconder")}
            cancelText={t("Cancelar")}
            onConfirm={handleDeleteOrIgnore}
            onCancel={(e) => e?.stopPropagation()}
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                ...(isEmployeeTask && { color: "var(--ant-error-color)" }),
              }}
            >
              {isEmployeeTask ? <DeleteOutlined /> : <EyeOutlined />}
              {isEmployeeTask ? t("Deletar") : t("Esconder")}
            </span>
          </Popconfirm>
        ),
        onClick: () => {}, // Empty onClick to prevent dropdown from closing
      });
    }

    return items;
  }, [isEmployeeTask, canDelete, t, toggleEditing, handleDeleteOrIgnore]);

  let childNode = children;
  if (dataIndex?.toString() === "actions") {
    childNode = (
      <>
        <Form.Item name="editing" hidden>
          <Input />
        </Form.Item>
        {editing ? (
          <Space size="middle">
            <Button
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              {...btnShared}
            />
            <Button
              icon={isNew ? <DeleteOutlined /> : <RollbackOutlined />}
              onClick={handleCancelEdit}
              danger={!!isNew}
              title={isNew ? t("Cancelar") : t("Cancelar edição")}
              {...btnShared}
            />
          </Space>
        ) : (
          <Dropdown
            menu={{
              items: menuItems,
              style: {
                borderRadius: "8px",
                overflow: "hidden",
              },
            }}
            trigger={["click"]}
            placement="bottomCenter"
          >
            <Button
              icon={<MoreOutlined />}
              size="small"
              style={{ border: 0 }}
            />
          </Dropdown>
        )}
      </>
    );
  }

  if (editing && dataIndex && dataIndex.toString() === "priority") {
    childNode = (
      <>
        <Form.Item
          name="priority"
          style={{ margin: 0 }}
          rules={[{ required: true, message: t("Selecione a prioridade") }]}
        >
          <Select
            style={{ width: "100%", minWidth: 150 }}
            placeholder={t("Selecione a prioridade")}
            options={TaskPrioritySchema.options.map((priority) => ({
              key: priority,
              value: priority,
              label: priorityNames[priority],
            }))}
          />
        </Form.Item>
      </>
    );
  }
  // only change evaluation selection if it's a new task
  if (
    canEvaluate &&
    editing &&
    dataIndex &&
    dataIndex.toString() === "evaluation" &&
    isNew
  ) {
    childNode = (
      <>
        <Form.Item name="evaluation" required={false} style={{ margin: 0 }}>
          <Select
            style={{ width: "100%", minWidth: 150 }}
            placeholder={t("Selecione uma avaliação")}
            allowClear
            options={evaluationOptions?.map((option) => ({
              key: option.id,
              value: option.id,
              label: (
                <PeopleCardTag
                  textColor={option.color_text}
                  color={option.color_tag}
                >
                  {option.name}
                </PeopleCardTag>
              ),
            }))}
          />
        </Form.Item>
      </>
    );
  }
  if (
    (isNew || (editing && canEditTemplate)) &&
    dataIndex &&
    dataIndex.toString() === ["template_task", "name"].toString()
  ) {
    childNode = (
      <Form.Item
        name="template_task_id"
        rules={[
          {
            required: true,
            message: t("Por favor, selecione um modelo de tarefa"),
          },
        ]}
        style={{ margin: 0 }}
      >
        <TemplateSelect
          record={record}
          usedTemplatesFilter={[
            {
              id: employeeId,
              model: "employee",
            },
            ...(positionId
              ? [
                  {
                    id: positionId,
                    model: "position",
                  },
                ]
              : []),
          ]}
        />
      </Form.Item>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
